import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState, type FormEvent } from 'react'
import { ApiError } from '../../api/client'
import { allowCfaApp, cfaQuery, saveCfa, type CfaMode, type CfaPage } from '../../api/settingsSecuritee'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'

/**
 * Settings > Securitee > Controlled Folder Access: PREVENIREA la ransomware,
 * prin Microsoft Defender (API: `App\Securitee\Cfa\ControlledFolderAccess`;
 * agentul: securitee/cfa.go).
 *
 * Treapta firmei (Off / Audit / Block), aplicatiile permise si folderele
 * protejate in plus, si - langa ele - programele pe care Defender le-a oprit
 * sau le-ar fi oprit, cu "Add to allowed list": in Audit adminul vede ce
 * trebuie permis INAINTE sa treaca pe Block. Treapta unui singur aparat se pune
 * din fisa lui (Securitee > Devices > Antiransomware).
 *
 * TEXTELE sunt propuneri (28 septembrie 2026), nu forma finala.
 */

export const CFA_MODES: { value: CfaMode; label: string; text: string }[] = [
  {
    value: 0,
    label: 'Off',
    text: 'OptimumDesk does not change Controlled Folder Access. Devices where it was turned on from here go back to the setting they had before.',
  },
  {
    value: 1,
    label: 'Audit',
    text: 'Nothing is blocked. Microsoft Defender records every change to a protected folder that it would have blocked. Those applications are listed below, so you can allow the ones you trust before you switch to Block.',
  },
  {
    value: 2,
    label: 'Block',
    text: 'Only allowed applications can change files in protected folders (Documents, Pictures, Videos, Music, Desktop, Favorites and the folders below). Any other application is stopped and reported.',
  },
]

export const CFA_INTRO =
  'Controlled Folder Access is the ransomware protection built into Microsoft Defender: it stops unknown applications from changing files in protected folders. Bait files and ransomware extensions only raise an alert; this setting prevents the damage.'

export const CFA_LIMITS =
  'Works only where Microsoft Defender is the active antivirus; on devices with another antivirus nothing is changed and an event says so. The OptimumDesk agent is always allowed. A Group Policy or Intune setting for Controlled Folder Access takes precedence over this one.'

const ACTION_LABEL: Record<string, string> = { blocked: 'Blocked', audited: 'Audit' }

function lines(text: string): string[] {
  return text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l !== '')
}

export function ControlledFolderAccessPanel() {
  const client = useQueryClient()
  const page = useQuery(cfaQuery())
  const [mode, setMode] = useState<CfaMode>(0)
  const [allowed, setAllowed] = useState('')
  const [folders, setFolders] = useState('')
  const [bad, setBad] = useState<string[]>([])
  const [busy, setBusy] = useState(false)

  const data = page.data
  useEffect(() => {
    if (!data) return
    setMode(data.mode)
    setAllowed(data.allowed.join('\n'))
    setFolders(data.protected.join('\n'))
  }, [data])

  if (page.error instanceof ApiError && page.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (page.error) return <div className="alert alert-danger">Something went wrong: {page.error.message}</div>
  if (!data) return <Spinner />

  const fresh = (body: CfaPage) => client.setQueryData(['settings', 'securitee', 'cfa'], body)

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (busy) return
    setBusy(true)
    setBad([])
    try {
      const body = await saveCfa(mode, lines(allowed), lines(folders))
      fresh(body)
      toast(body.message, 'Success', 'success')
    } catch (err) {
      if (err instanceof ApiError) {
        const list = [...((err.details?.badApps as string[] | undefined) ?? []), ...((err.details?.badFolders as string[] | undefined) ?? [])]
        setBad(list)
        toast(err.message, 'Error', 'error')
      } else {
        toast('Something went wrong.', 'Error', 'error')
      }
    } finally {
      setBusy(false)
    }
  }

  const allow = async (path: string) => {
    if (busy) return
    setBusy(true)
    try {
      const body = await allowCfaApp(path)
      fresh(body)
      toast(body.message, 'Success', 'success')
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Something went wrong.', 'Error', 'error')
    } finally {
      setBusy(false)
    }
  }

  const readOnly = !data.canWrite
  return (
    <div id="content" className="cfa-panel">
      <header className="panel-heading">
        <h3>
          <strong>Controlled Folder</strong> Access
        </h3>
        <label className="color">ransomware prevention with Microsoft Defender</label>
      </header>

      <p className="cfa-intro">{CFA_INTRO}</p>

      {!data.ready && <div className="alert alert-warning cfa-not-migrated">The Controlled Folder Access tables are missing. The database migrations must be run first.</div>}

      <form className="cfa-form" onSubmit={onSubmit}>
        <table className="table cfa-modes">
          <tbody>
            {CFA_MODES.map((m) => (
              <tr key={m.value} className={mode === m.value ? 'active' : undefined}>
                <td style={{ width: 140 }}>
                  <label style={{ fontWeight: 600 }}>
                    <input
                      type="radio"
                      name="mode"
                      value={m.value}
                      checked={mode === m.value}
                      disabled={readOnly}
                      onChange={() => setMode(m.value)}
                      aria-label={`Controlled Folder Access: ${m.label}`}
                    />{' '}
                    {m.label}
                  </label>
                </td>
                <td className="cfa-mode-text" style={{ color: '#7f8c8d' }}>
                  {m.text}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="cfa-limits text-muted" style={{ fontSize: 12 }}>
          {CFA_LIMITS}
        </p>

        <div className="row">
          <div className="col-md-6 form-group">
            <label htmlFor="cfa-allowed">Allowed applications</label>
            <textarea
              id="cfa-allowed"
              className="form-control cfa-allowed"
              rows={6}
              value={allowed}
              readOnly={readOnly}
              onChange={(e) => setAllowed(e.target.value)}
              placeholder={'C:\\Program Files\\Vendor\\app.exe'}
            />
            <span className="help-block">Full path of each .exe file, one per line.</span>
          </div>
          <div className="col-md-6 form-group">
            <label htmlFor="cfa-folders">Additional protected folders</label>
            <textarea
              id="cfa-folders"
              className="form-control cfa-folders"
              rows={6}
              value={folders}
              readOnly={readOnly}
              onChange={(e) => setFolders(e.target.value)}
              placeholder={'D:\\Data'}
            />
            <span className="help-block">Full folder paths, one per line. The Windows default folders are always protected.</span>
          </div>
        </div>

        {bad.length > 0 && (
          <div className="alert alert-danger cfa-bad">
            These entries are not full paths the agent can use: {bad.join(', ')}
          </div>
        )}

        {!readOnly && <input type="submit" className="btn btn-primary cfa-save" value="Save" disabled={busy} />}
      </form>

      <h4 className="bordered" style={{ marginTop: 24 }}>
        Applications seen in Audit and Block <small>(last {data.seenDays} days)</small>
      </h4>
      {data.seen.length === 0 ? (
        <p className="text-muted cfa-seen-empty">No application has been blocked or audited yet.</p>
      ) : (
        <table className="table table-striped cfa-seen">
          <thead>
            <tr>
              <th>Application</th>
              <th>Action</th>
              <th>Times</th>
              <th>Devices</th>
              <th>Last seen</th>
              <th>Last file</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {data.seen.map((app) => (
              <tr key={app.action + app.process}>
                <td className="cfa-process" style={{ wordBreak: 'break-all' }}>
                  {app.process}
                </td>
                <td>
                  <span className={app.action === 'blocked' ? 'label label-danger' : 'label label-warning'}>{ACTION_LABEL[app.action] ?? app.action}</span>
                </td>
                <td>{app.hits}</td>
                <td>{app.devices}</td>
                <td>{app.lastSeen}</td>
                <td style={{ wordBreak: 'break-all' }}>{app.lastFile}</td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {app.allowed ? (
                    <span className="label label-success cfa-allowed-label">Allowed</span>
                  ) : data.canWrite && app.canAllow ? (
                    <button type="button" className="btn btn-default btn-xs cfa-allow" disabled={busy} onClick={() => void allow(app.process)}>
                      Add to allowed list
                    </button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}
